<template>
  <div class="card shadow-sm table-card mb-4">
    <div class="form-card-header">
      <div class="section-number"><i class="fe fe-shield" style="font-size: 12px;"></i></div>
      <div class="flex-grow-1">
        <h6 class="mb-0 font-weight-bold text-white">{{ title }}</h6>
        <small style="color: rgba(255,255,255,.75);">Facility, expiry date, HMO, programme, ID number and enabled/disabled changes on enrollees</small>
      </div>
    </div>
    <div class="card-body pb-0" v-if="showFilters">
      <div class="form-row">
        <div class="form-group col-md-3">
          <label class="small font-weight-bold text-secondary">Change</label>
          <select class="form-control form-control-sm" v-model="field" @change="load(1)">
            <option value="">All sensitive changes</option>
            <option v-for="(label, key) in fields" :key="key" :value="key">{{ label }}</option>
          </select>
        </div>
        <div class="form-group col-md-3">
          <label class="small font-weight-bold text-secondary">From</label>
          <input type="date" class="form-control form-control-sm" v-model="from" @change="load(1)" />
        </div>
        <div class="form-group col-md-3">
          <label class="small font-weight-bold text-secondary">To</label>
          <input type="date" class="form-control form-control-sm" v-model="to" @change="load(1)" />
        </div>
      </div>
    </div>
    <div class="table-responsive">
      <table class="table mb-0">
        <thead class="table-head">
          <tr>
            <th class="table-th">When</th>
            <th class="table-th" v-if="!causerId">Changed by</th>
            <th class="table-th">Enrollee</th>
            <th class="table-th">Change</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td colspan="4" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</td></tr>
          <tr v-else-if="error"><td colspan="4" class="empty-state">{{ error }}</td></tr>
          <tr v-else-if="!rows.length"><td colspan="4" class="empty-state">No sensitive changes found.</td></tr>
          <tr class="table-row" v-for="r in rows" :key="r.id">
            <td class="text-nowrap">{{ r.at }}</td>
            <td v-if="!causerId">
              <router-link v-if="r.causer.id && r.causer.type === 'employee'" :to="`/employee/${r.causer.id}`">{{ r.causer.name }}</router-link>
              <span v-else>{{ r.causer.name }}</span>
              <div class="small text-muted">{{ r.causer.job_title }}</div>
            </td>
            <td>
              <router-link :to="r.subject.type === 'dependant' ? `/dependent/${r.subject.id}` : `/client/${r.subject.id}`">{{ r.subject.name || "Enrollee #" + r.subject.id }}</router-link>
              <div class="small text-muted">{{ r.subject.type === "dependant" ? "Dependant" : "Principal" }}<span v-if="r.subject.id_card_number"> · {{ r.subject.id_card_number }}</span></div>
            </td>
            <td>
              <div v-for="c in r.changes" :key="c.field" class="small">
                <b>{{ c.label }}:</b> <span class="text-muted">{{ c.old_label }}</span> <i class="fe fe-arrow-right mx-1"></i> <b>{{ c.new_label }}</b>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="card-footer bg-white d-flex justify-content-between align-items-center" v-if="meta.last_page > 1">
      <small class="text-muted">Page {{ meta.current_page }} of {{ meta.last_page }} · {{ meta.total }} changes</small>
      <div>
        <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="meta.current_page <= 1" @click="load(meta.current_page - 1)">Previous</button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page >= meta.last_page" @click="load(meta.current_page + 1)">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Sensitive changes to enrollees from the activity log (GET audit/sensitive-changes).
 * causerId = changes made by one staff member (single employee page); userId = changes to one enrollee.
 */
export default {
  name: "SensitiveChanges",
  props: {
    causerId: { type: [Number, String], default: null },
    userId: { type: [Number, String], default: null },
    title: { type: String, default: "Sensitive changes" },
    showFilters: { type: Boolean, default: true },
    perPage: { type: Number, default: 20 },
  },
  data() {
    return {
      field: "",
      from: "",
      to: "",
      rows: [],
      fields: {
        provider_id: "Facility", expiry_date: "Expiry date", org_id: "HMO", sector: "Programme", id_card_number: "ID card number", disabled: "Disabled", enabled: "Enabled", deleted: "Deleted",
      },
      meta: { current_page: 1, last_page: 1, total: 0 },
      loading: false,
      error: "",
    };
  },
  watch: {
    causerId() { this.load(1); },
    userId() { this.load(1); },
  },
  mounted() {
    this.load(1);
  },
  methods: {
    load(page) {
      this.loading = true;
      this.error = "";
      this.axios
        .get("/api/v1/auth/audit/sensitive-changes", {
          params: {
            page, per_page: this.perPage, field: this.field || undefined, from: this.from || undefined, to: this.to || undefined,
            causer_id: this.causerId || undefined, user_id: this.userId || undefined,
          },
        })
        .then((res) => {
          this.rows = res.data.data;
          this.meta = { current_page: res.data.current_page, last_page: res.data.last_page, total: res.data.total };
          if (res.data.fields) this.fields = res.data.fields;
        })
        .catch((e) => { this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the audit log."; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
@import "../reports.css";
</style>
